import React, { FC, useCallback, useMemo, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { FlatList, LayoutChangeEvent, Platform, View, useWindowDimensions } from 'react-native';

import { AppDownloadSection } from 'components/features/components/app-download-section';
import { MarketingTileGroup } from 'components/features/components/marketing-tile-group';
import { RewardsSection } from 'components/features/components/rewards-section';
import { SimpleHomePage } from 'components/features/components/simple-home-page';
import { Footer } from 'components/footer';
import { LayoutContainer } from 'components/layout/styled';
import LoadingAnimation from 'components/loading-animation';
import { QuestActivationSource, QuestSection } from 'components/quests';
import { IMarketingCardGroupFragment, IMarketingTileGroup } from 'generated/sanity-graphql';
import { useIsDesktopLargeBreakpoint, useIsMobileBreakpoint } from 'hooks/use-media-query';
import { primitive } from 'styles/constants';
import { isNative } from 'utils/environment';

import { Section, Spacer } from './home-page/styles';
import { Component, Components } from './home-page/types';
import { useAlertToast } from './home-page/use-alert-toast';

const itemIdExtractor = (item: Component | null, index: number) => item?._id || index + '_item';

const Spinner = () => (
  <LayoutContainer>
    <Box padding="$2">
      <LoadingAnimation />
    </Box>
  </LayoutContainer>
);

let previousHeight = 0;
const APPROXIMATE_CHROME_HEIGHT_IOS = 213;
const APPROXIMATE_CHROME_HEIGHT_ANDROID = 144;

export const Features: FC<React.PropsWithChildren<{
  components: Components;
  headerComponent?: React.ReactElement | null;
}>> = React.memo(({ headerComponent, components }) => {
  const { alertComponent, filteredComponents } = React.useMemo(() => {
    const alert = components?.find(c => c?.__typename === 'Alert');
    const filtered = components?.filter(c => c?.__typename !== 'Alert') || [];
    return { alertComponent: alert, filteredComponents: filtered };
  }, [components]);

  const componentLength = filteredComponents?.length || 0;
  const isMobile = useIsMobileBreakpoint();
  const isDesktopLarge = useIsDesktopLargeBreakpoint();
  const { height, width } = useWindowDimensions();
  const [flatListDimensions, setFlatListDimensions] = useState<{
    height: number;
    width: number;
  } | null>({
    // prevent large amounts of jank as the screen is resized based on various nav bar conditions
    height:
      previousHeight ||
      height -
        (Platform.OS === 'web'
          ? 0
          : Platform.OS === 'ios'
          ? APPROXIMATE_CHROME_HEIGHT_IOS
          : APPROXIMATE_CHROME_HEIGHT_ANDROID),
    width,
  });

  previousHeight = flatListDimensions?.height || 0;

  const renderItem = useCallback(
    ({ item, index }: { item: Component | null; index: number }) => {
      if (!item) {
        return null;
      }

      const first = index === 0;
      const last = index + 1 === componentLength;
      switch (item.__typename) {
        case 'Spacer': {
          return <Spacer $isSmall={item.isSmall} />;
        }

        case 'SimpleHomePage': {
          return <SimpleHomePage item={item} flatListDimensions={flatListDimensions} />;
        }

        case 'MarketingCardGroup':
          return (
            <LayoutContainer mt={'$4'}>
              <Section first={first} last={last} shouldRenderDefaultBackground={true}>
                <MarketingTileGroup item={(item as unknown) as IMarketingCardGroupFragment} />
              </Section>
            </LayoutContainer>
          );

        case 'MarketingTileGroup':
          return (
            <LayoutContainer>
              <Section first={first} last={last}>
                <MarketingTileGroup item={item as IMarketingTileGroup} />
              </Section>
            </LayoutContainer>
          );

        case 'RewardsSection': {
          return (
            <LayoutContainer isFullContainer={isMobile}>
              <RewardsSection item={item} />
            </LayoutContainer>
          );
        }

        case 'QuestsSection': {
          return (
            <LayoutContainer>
              <QuestSection activationSource={QuestActivationSource.HOME_PAGE} />
            </LayoutContainer>
          );
        }

        case 'AppDownload': {
          return <AppDownloadSection item={item.details} />;
        }

        case 'Footer': {
          return <Footer />;
        }

        default: {
          return null;
        }
      }
    },
    [componentLength, isMobile, flatListDimensions]
  );

  const [isLoadingMore, setIsLoadingMore] = useState(true);
  const setIsLoadingMoreFalse = useCallback(() => setIsLoadingMore(false), []);

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => setFlatListDimensions(e.nativeEvent.layout),
    []
  );

  const hasHeader = !!headerComponent;

  useAlertToast(alertComponent);

  const HeaderElement = useMemo(() => {
    if (!hasHeader) {
      return null;
    }

    return <View style={{ backgroundColor: primitive.$mayoEggWhite }}>{headerComponent}</View>;
  }, [hasHeader, headerComponent]);

  if (!components) {
    return null;
  }

  return (
    <FlatList
      onLayout={onLayout}
      ListHeaderComponent={HeaderElement}
      ListFooterComponent={isLoadingMore ? Spinner : null}
      keyExtractor={itemIdExtractor}
      data={filteredComponents}
      initialNumToRender={2}
      stickyHeaderIndices={hasHeader ? [0] : []}
      renderItem={renderItem}
      onEndReached={setIsLoadingMoreFalse}
      // Changed from 0 to 0.01 because with 0 onEndReached was not being called on web
      // 0.01 is a small value, close to the original, that works on both web and mobile
      onEndReachedThreshold={0.01}
      {...(isNative && {
        windowSize: 1,
      })}
      showsVerticalScrollIndicator={isDesktopLarge}
    />
  );
});
